import { useState, useEffect } from 'react';
import { api, apiFetch } from '../utils/api';

export default function Dashboard() {
  const [stats1d, setStats1d] = useState({});
  const [stats7d, setStats7d] = useState({});
  const [stats30d, setStats30d] = useState({});
  const [session, setSession] = useState(null);
  const [recentAds, setRecentAds] = useState([]);
  const [miningByDay, setMiningByDay] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoverBar, setHoverBar] = useState(null);

  useEffect(() => {
    Promise.all([
      api.getAdStats('1d').catch(() => ({})),
      api.getAdStats('7d').catch(() => ({})),
      api.getAdStats('30d').catch(() => ({})),
      api.getActiveSession().catch(() => ({ session: null })),
      api.getAds({ limit: 8, sort: 'mined_at_desc' }).catch(() => ({ data: [] })),
      apiFetch('/api/insights/mining-by-day?period=14d').catch(() => ({ data: [] })),
    ]).then(([s1, s7, s30, sess, ads, mining]) => {
      setStats1d(s1); setStats7d(s7); setStats30d(s30);
      setSession(sess.session);
      setRecentAds(ads.data || []);
      setMiningByDay(mining.data || []);
      setLoading(false);
    });
    const iv = setInterval(() => {
      api.getActiveSession().then(d => setSession(d.session)).catch(() => {});
    }, 10000);
    return () => clearInterval(iv);
  }, []);

  // Stale: running mas iniciou há mais de 30min sem scrolls, ou há mais de 2h com scrolls
  const sessionAge = session?.started_at ? (Date.now() - new Date(session.started_at).getTime()) / 1000 / 60 : 0;
  const isStale = session && session.status === 'running' && (
    ((session.scrolls_done || 0) === 0 && sessionAge > 10) ||
    (sessionAge > 120)
  );
  const isActive = session && session.status === 'running' && !isStale;

  const fmt = (n) => n != null ? n.toLocaleString() : '0';
  const timeAgo = (dt) => {
    if (!dt) return '';
    const diff = (Date.now() - new Date(dt).getTime()) / 1000;
    if (diff < 60) return 'agora';
    if (diff < 3600) return `${Math.floor(diff / 60)}min`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  };

  if (loading) return <div style={s.page}><div style={s.loading}>Carregando...</div></div>;

  const maxCount = Math.max(...miningByDay.map(d => d.count), 1);
  const today = stats1d.period_count || 0;
  const yesterday = miningByDay.length >= 2 ? miningByDay[miningByDay.length - 2]?.count || 0 : 0;
  const todayDiff = yesterday > 0 ? ((today - yesterday) / yesterday * 100).toFixed(0) : null;

  return (
    <div style={s.page}>
      {/* KPI Row */}
      <div style={s.kpiRow}>
        <div style={{ ...s.kpi, borderLeft: '3px solid var(--accent)' }}>
          <div style={s.kpiTop}>
            <span style={s.kpiLabel}>Hoje</span>
            {todayDiff !== null && (
              <span style={{ fontSize: 11, fontWeight: 600, color: todayDiff >= 0 ? 'var(--accent-g)' : 'var(--accent-r)' }}>
                {todayDiff >= 0 ? '+' : ''}{todayDiff}%
              </span>
            )}
          </div>
          <div style={{ ...s.kpiValue, color: 'var(--accent)' }}>{fmt(today)}</div>
          <div style={s.kpiSub}>ads minerados</div>
        </div>
        <div style={{ ...s.kpi, borderLeft: '3px solid var(--accent-g)' }}>
          <div style={s.kpiLabel}>7 dias</div>
          <div style={s.kpiValue}>{fmt(stats7d.period_count || 0)}</div>
          <div style={s.kpiSub}>{fmt(stats7d.unique_offers || 0)} offers</div>
        </div>
        <div style={{ ...s.kpi, borderLeft: '3px solid var(--accent-p)' }}>
          <div style={s.kpiLabel}>30 dias</div>
          <div style={s.kpiValue}>{fmt(stats30d.period_count || 0)}</div>
          <div style={s.kpiSub}>{fmt(stats30d.cloakers || 0)} cloakers</div>
        </div>
        <div style={{ ...s.kpi, borderLeft: '3px solid var(--accent-a)' }}>
          <div style={s.kpiLabel}>Total</div>
          <div style={s.kpiValue}>{fmt(stats30d.total_count || 0)}</div>
          <div style={s.kpiSub}>{fmt(stats30d.unique_offers || 0)} offers unicas</div>
        </div>
      </div>

      {/* Middle: Chart + Bot */}
      <div style={s.middleRow}>
        {/* Chart */}
        <div style={s.card}>
          <div style={s.cardHeader}>
            <span style={s.cardTitle}>Mineracao</span>
            <span style={s.cardSub}>Ultimos 14 dias</span>
          </div>
          <div style={s.chart}>
            {miningByDay.map((d, i) => (
              <div key={i} style={s.chartCol}
                onMouseEnter={() => setHoverBar(i)} onMouseLeave={() => setHoverBar(null)}>
                {hoverBar === i && (
                  <div style={s.chartTooltip}>{d.count} ads</div>
                )}
                <div style={s.chartBarWrap}>
                  <div style={{
                    ...s.chartBar,
                    height: `${Math.max((d.count / maxCount) * 100, 4)}%`,
                    background: hoverBar === i ? 'var(--accent)' : i === miningByDay.length - 1 ? 'var(--accent)' : d.count > 0 ? 'var(--accent-p)' : 'var(--border)',
                    opacity: hoverBar === i || i === miningByDay.length - 1 ? 1 : 0.5,
                  }} />
                </div>
                <span style={s.chartLabel}>{d.date.slice(8)}/{d.date.slice(5, 7)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Bot Status */}
        <div style={s.card}>
          <div style={s.cardHeader}>
            <span style={s.cardTitle}>Bot</span>
            <span style={{ ...s.statusPill, background: isActive ? 'rgba(16,185,129,0.1)' : 'rgba(92,97,120,0.1)', color: isActive ? 'var(--accent-g)' : 'var(--text3)' }}>
              {isActive ? 'Online' : 'Offline'}
            </span>
          </div>
          {session ? (
            <div style={s.botGrid}>
              <div style={s.botStat}>
                <div style={s.botStatValue}>{session.scrolls_done || 0}</div>
                <div style={s.botStatLabel}>Scrolls</div>
              </div>
              <div style={s.botStat}>
                <div style={s.botStatValue}>{session.ads_found || 0}</div>
                <div style={s.botStatLabel}>Encontrados</div>
              </div>
              <div style={s.botStat}>
                <div style={s.botStatValue}>{session.ads_saved || 0}</div>
                <div style={s.botStatLabel}>Salvos</div>
              </div>
              <div style={s.botStat}>
                <div style={s.botStatValue}>{timeAgo(session.started_at)}</div>
                <div style={s.botStatLabel}>{isActive ? 'Rodando' : 'Ultima sessao'}</div>
              </div>
            </div>
          ) : (
            <div style={{ color: 'var(--text3)', fontSize: 11, padding: '20px 0', textAlign: 'center' }}>
              Nenhuma sessao registrada
            </div>
          )}
        </div>
      </div>

      {/* Recent Ads */}
      <div style={s.card}>
        <div style={s.cardHeader}>
          <span style={s.cardTitle}>Ultimos ads</span>
          <span style={s.cardSub}>{recentAds.length} mais recentes</span>
        </div>
        {recentAds.length === 0 ? (
          <div style={{ color: 'var(--text3)', fontSize: 13, padding: 20, textAlign: 'center' }}>Nenhum ad minerado</div>
        ) : (
          <div style={s.adGrid}>
            {recentAds.map((ad, i) => (
              <div key={i} style={s.adCard}>
                {/* Thumbnail */}
                <div style={s.adThumb}>
                  {(ad.thumbnail_path || ad.screenshot_feed_path) ? (
                    <img src={ad.thumbnail_path || ad.screenshot_feed_path} style={s.adThumbImg}
                      onError={e => { e.target.style.display = 'none'; }} />
                  ) : (
                    <div style={s.adThumbEmpty} />
                  )}
                </div>
                {/* Info */}
                <div style={s.adCardBody}>
                  <div style={s.adCardName}>{ad.fb_page_name || '?'}</div>
                  <div style={s.adCardMeta}>
                    {ad.nicho && <span style={s.nicheBadge}>{ad.nicho}</span>}
                    {ad.has_cloaker && <span style={s.cloakerBadge}>CLOAKER</span>}
                  </div>
                  <div style={s.adCardStats}>
                    <span>Likes {ad.likes || 0}</span>
                    <span>Comm {ad.comments || 0}</span>
                    <span>{timeAgo(ad.mined_at)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  page: { padding: '24px', display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 1200, margin: '0 auto' },
  loading: { padding: 60, textAlign: 'center', color: 'var(--text3)', fontSize: 14 },

  // KPI
  kpiRow: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 },
  kpi: { background: 'var(--surface)', borderRadius: 8, padding: '16px 20px', border: '1px solid var(--border)' },
  kpiTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  kpiLabel: { fontSize: 11, color: 'var(--text3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3 },
  kpiValue: { fontSize: 24, fontWeight: 700, color: 'var(--text)', marginTop: 4, lineHeight: 1 },
  kpiSub: { fontSize: 11, color: 'var(--text3)', marginTop: 6 },

  // Middle
  middleRow: { display: 'grid', gridTemplateColumns: '5fr 2fr', gap: 12 },

  // Card
  card: { background: 'var(--surface)', borderRadius: 8, padding: '16px 20px', border: '1px solid var(--border)' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  cardTitle: { fontSize: 13, fontWeight: 600, color: 'var(--text)' },
  cardSub: { fontSize: 11, color: 'var(--text3)' },

  // Chart
  chart: { display: 'flex', gap: 6, alignItems: 'flex-end', height: 160, position: 'relative' },
  chartCol: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, cursor: 'pointer', position: 'relative' },
  chartBarWrap: { flex: 1, width: '100%', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' },
  chartBar: { width: '55%', borderRadius: '3px 3px 0 0', minHeight: 3, transition: 'all 0.2s' },
  chartLabel: { fontSize: 10, color: 'var(--text3)', whiteSpace: 'nowrap' },
  chartTooltip: {
    position: 'absolute', top: -24, left: '50%', transform: 'translateX(-50%)',
    padding: '2px 8px', borderRadius: 4, background: 'var(--text)', color: 'var(--bg)',
    fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap', zIndex: 10,
  },

  // Bot Status
  statusPill: { padding: '3px 10px', borderRadius: 100, fontSize: 11, fontWeight: 600 },
  botGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 8 },
  botStat: { textAlign: 'center', padding: '12px 0' },
  botStatValue: { fontSize: 16, fontWeight: 700, color: 'var(--text)' },
  botStatLabel: { fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.3, marginTop: 2 },

  // Recent Ads
  adGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 8 },
  adCard: { display: 'flex', gap: 10, padding: '10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface2)' },
  adThumb: { width: 56, height: 56, borderRadius: 6, overflow: 'hidden', flexShrink: 0, background: 'var(--surface3)' },
  adThumbImg: { width: '100%', height: '100%', objectFit: 'cover' },
  adThumbEmpty: { width: '100%', height: '100%', background: 'var(--surface3)' },
  adCardBody: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 },
  adCardName: { fontSize: 11, fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  adCardMeta: { display: 'flex', gap: 4 },
  nicheBadge: { fontSize: 10, padding: '1px 6px', borderRadius: 3, background: 'rgba(167,139,250,0.1)', color: 'var(--accent-p)', fontWeight: 600 },
  cloakerBadge: { fontSize: 10, padding: '1px 6px', borderRadius: 3, background: 'rgba(244,63,94,0.1)', color: 'var(--accent-r)', fontWeight: 600 },
  adCardStats: { display: 'flex', gap: 8, fontSize: 10, color: 'var(--text3)' },
};
